/**
 * Подключение помощника: выбор адресата, ссылка, QR.
 *
 * Разные помощники принимают подключение по-разному, и одной строки на всех не
 * бывает. Поэтому адресат выбирается до выдачи, а на выходе — то, что этот
 * адресат понимает: команда, кусок настроек или адрес с заголовком.
 *
 * Токен показывается ОДИН раз. У консоли остаётся только его хеш, и «покажите
 * ещё раз» — это выдать новый и отозвать старый. Так честнее, чем хранить
 * секрет ради удобства, которое нужно раз в жизни ключа.
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';

const TARGETS = [
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'claude-desktop', label: 'Claude Desktop' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'raw', label: 'Своими руками' },
] as const;

interface Issued {
  kid: string;
  label: string;
  url: string;
  token: string;
  title: string;
  command: string;
  note: string;
  qr: string;
}

interface KeyRow {
  kid: string;
  label: string;
  createdAt: string;
  lastSeenAt: string | null;
  calls: number;
  revokedAt: string | null;
  revokedReason: string | null;
}

export function McpKeys(): React.JSX.Element {
  const { data, reload } = useQuery<{ items: KeyRow[] }>('mcp.list');
  const [label, setLabel] = useState('');
  const [target, setTarget] = useState<string>('claude-code');
  const [issued, setIssued] = useState<Issued | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function issue(): Promise<void> {
    setBusy(true); setFailure(null);
    try {
      setIssued(await request<Issued>('mcp.issue', { label: label.trim(), target }));
      setLabel('');
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось');
    } finally { setBusy(false); }
  }

  async function revoke(kid: string): Promise<void> {
    setBusy(true); setFailure(null);
    try { await request('mcp.revoke', { kid }); reload(); }
    catch (err) { setFailure((err as { msg?: string }).msg ?? 'не получилось'); }
    finally { setBusy(false); }
  }

  const keys = data?.items ?? [];

  return (
    <section className="mcp">
      <h3 className="mcp__title">Помощники</h3>
      <p className="muted mcp__lead">
        Помощник отвечает на вопросы о копиях и умеет запустить копирование вне
        расписания. Восстановление наружу не отдаётся; опасные действия просят
        код второго фактора.
      </p>

      <div className="mcp__row">
        <input
          className="input" placeholder="кому выдаём, например «Claude на ноутбуке»"
          value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60}
        />
        <select className="input" value={target} onChange={(e) => setTarget(e.target.value)}>
          {TARGETS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <button type="button" className="button" disabled={busy || label.trim().length < 2}
                onClick={() => void issue()}>
          Выдать ключ
        </button>
      </div>

      {issued && (
        <div className="mcp__issued">
          <h4>{issued.title}</h4>
          <p className="muted">{issued.note}</p>

          <pre className="mcp__command mono">{issued.command}</pre>
          <div className="mcp__row">
            <button
              type="button" className="button button--ghost"
              onClick={() => {
                void navigator.clipboard?.writeText(issued.command);
                setCopied(true);
              }}
            >
              {copied ? 'Скопировано' : 'Скопировать'}
            </button>
            <button type="button" className="button button--ghost" onClick={() => setIssued(null)}>
              Сохранил
            </button>
          </div>

          <div className="mcp__qr-wrap">
            <div className="mcp__qr" dangerouslySetInnerHTML={{ __html: issued.qr }} />
            <p className="muted">
              Для телефона. Внутри — адрес консоли и токен; строку в полсотни
              знаков с экрана не перепечатывают.
            </p>
          </div>

          <p className="mcp__warn">
            Токен показан <b>один раз</b>: у консоли остаётся только его хеш.
            Потеряли — выдайте новый и отзовите этот.
          </p>
        </div>
      )}

      {keys.length > 0 && (
        <ul className="mcp__keys">
          {keys.map((k) => (
            <li key={k.kid} className={k.revokedAt ? 'mcp__key mcp__key--dead' : 'mcp__key'}>
              <span className="mcp__key-label">{k.label}</span>
              <span className="mono muted">{k.kid}</span>
              <span className="muted">
                {k.revokedAt ? `отозван — ${k.revokedReason ?? ''}`
                  : k.calls === 0 ? 'ни разу не пользовались'
                  : `вызовов ${k.calls}, последний ${new Date(k.lastSeenAt!).toLocaleString('ru-RU')}`}
              </span>
              {!k.revokedAt && (
                <button type="button" className="button button--ghost button--small button--danger"
                        disabled={busy} onClick={() => void revoke(k.kid)}>
                  Отозвать
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {failure && <p className="alert alert--error">{failure}</p>}
    </section>
  );
}
